import { NavLink, Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom';

import { I18nProvider } from '@/lib/i18n';
import { portalForHost } from '@/lib/portal-host';

import { Suspense, lazy, useEffect, useState } from 'react';

import { fetchDishes } from './features/owner/api';
import { DishScreen } from './features/menu/DishScreen';
import { EntryScreen } from './features/menu/EntryScreen';
import { MenuScreen } from './features/menu/MenuScreen';

const EntrySettingsScreen = lazy(() =>
  import('./features/owner/EntrySettingsScreen').then((m) => ({
    default: m.EntrySettingsScreen,
  })),
);
const PhotoScreen = lazy(() =>
  import('./features/owner/PhotoScreen').then((m) => ({ default: m.PhotoScreen })),
);
const MenuSettingsScreen = lazy(() =>
  import('./features/owner/MenuSettingsScreen').then((m) => ({
    default: m.MenuSettingsScreen,
  })),
);
const TextEntryScreen = lazy(() =>
  import('./features/owner/TextEntryScreen').then((m) => ({
    default: m.TextEntryScreen,
  })),
);

const AdminApp = lazy(() =>
  import('./features/admin/AdminApp').then((m) => ({ default: m.AdminApp })),
);
const HostPortal = lazy(() =>
  import('./features/portal/HostPortal').then((m) => ({ default: m.HostPortal })),
);
const AskScreen = lazy(() =>
  import('./features/menu/AskScreen').then((m) => ({ default: m.AskScreen })),
);
const OptionScreen = lazy(() =>
  import('./features/menu/OptionScreen').then((m) => ({ default: m.OptionScreen })),
);
const RequireOwner = lazy(() =>
  import('./features/owner/RequireOwner').then((m) => ({ default: m.RequireOwner })),
);
const ItemEditingScreen = lazy(() =>
  import('./features/owner/ItemEditingScreen').then((m) => ({
    default: m.ItemEditingScreen,
  })),
);
const PasswordScreen = lazy(() =>
  import('./features/owner/PasswordScreen').then((m) => ({ default: m.PasswordScreen })),
);
const SignageScreen = lazy(() =>
  import('./features/owner/SignageScreen').then((m) => ({ default: m.SignageScreen })),
);
const Gallery = lazy(() =>
  import('./components/dev/Gallery').then((m) => ({ default: m.Gallery })),
);

export function App() {
  return (
    <I18nProvider>
      <Routes>
      
      
      <Route
        path="/"
        element={
          <Suspense fallback={<RouteLoading />}>
            <RootByHost />
          </Suspense>
        }
      />
      
      <Route
        path="/admin/*"
        element={
          <Suspense fallback={<RouteLoading />}>
            <HostPortal requires="admin">
              <AdminApp />
            </HostPortal>
          </Suspense>
        }
      />

      
      {(['customer', 'owner', 'admin-portal'] as const).map((role) => (
        <Route
          key={role}
          path={`/${role}`}
          element={
            <Navigate
              to={
                role === 'customer'
                  ? '/shop/shop_A/menu'
                  : role === 'owner'
                    ? '/shop/shop_A/manage'
                    : '/admin'
              }
              replace
            />
          }
        />
      ))}

      <Route
        path="/dev/gallery"
        element={
          <Suspense fallback={<RouteLoading />}>
            <Gallery />
          </Suspense>
        }
      />

      <Route path="/shop/:slug" element={<SlugEntry />} />
      <Route path="/shop/:slug/menu" element={<SlugMenu />} />
      <Route path="/shop/:slug/dish/:dishId" element={<SlugDish />} />
      
      <Route
        path="/shop/:slug/option"
        element={
          <Suspense fallback={<RouteLoading />}>
            <SlugOption />
          </Suspense>
        }
      />
      
      <Route
        path="/shop/:slug/ask"
        element={
          <Suspense fallback={<RouteLoading />}>
            <SlugAsk />
          </Suspense>
        }
      />
      <Route
        path="/shop/:slug/ask/:dishId"
        element={
          <Suspense fallback={<RouteLoading />}>
            <SlugAsk />
          </Suspense>
        }
      />
      <Route
        path="/shop/:slug/option/:dishId"
        element={
          <Suspense fallback={<RouteLoading />}>
            <SlugOption />
          </Suspense>
        }
      />

      
      <Route path="/shop/:slug/manage" element={<RequireOwnerRoute><SlugOwnerHome /></RequireOwnerRoute>} />
      <Route path="/shop/:slug/manage/entry" element={<RequireOwnerRoute><Suspense fallback={<RouteLoading />}><SlugOwnerEntry /></Suspense></RequireOwnerRoute>} />
      <Route path="/shop/:slug/manage/photo" element={<RequireOwnerRoute><Suspense fallback={<RouteLoading />}><SlugOwnerPhoto /></Suspense></RequireOwnerRoute>} />
      <Route path="/shop/:slug/manage/menu" element={<RequireOwnerRoute><Suspense fallback={<RouteLoading />}><SlugOwnerMenu /></Suspense></RequireOwnerRoute>} />
      <Route path="/shop/:slug/manage/dish/:dishId" element={<RequireOwnerRoute><Suspense fallback={<RouteLoading />}><SlugOwnerText /></Suspense></RequireOwnerRoute>} />
      <Route path="/shop/:slug/manage/dish" element={<RequireOwnerRoute><Suspense fallback={<RouteLoading />}><SlugOwnerText /></Suspense></RequireOwnerRoute>} />
      <Route path="/shop/:slug/manage/signage" element={<RequireOwnerRoute><Suspense fallback={<RouteLoading />}><SlugOwnerSignage /></Suspense></RequireOwnerRoute>} />
      
      <Route path="/shop/:slug/manage/edit" element={<RequireOwnerRoute><Suspense fallback={<RouteLoading />}><SlugOwnerEdit /></Suspense></RequireOwnerRoute>} />
      <Route path="/shop/:slug/manage/password" element={<RequireOwnerRoute><Suspense fallback={<RouteLoading />}><SlugOwnerPassword /></Suspense></RequireOwnerRoute>} />

      
      <Route path="/:slug" element={<SlugEntry />} />

      <Route path="*" element={<NotFound />} />
      </Routes>
    </I18nProvider>
  );
}

function RouteLoading() {
  return (
    <main className="mx-auto max-w-[560px] px-4 py-8" aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading</span>
      <div className="h-6 w-40 animate-pulse rounded bg-ink-100" />
      <div className="mt-4 h-24 animate-pulse rounded-[var(--radius-lg)] bg-ink-100" />
      <div className="mt-3 h-24 animate-pulse rounded-[var(--radius-lg)] bg-ink-100" />
    </main>
  );
}

function SlugEntry() {
  const { slug = '' } = useParams();
  return <EntryScreen shopSlug={slug} />;
}

function SlugMenu() {
  const { slug = '' } = useParams();
  return <MenuScreen shopSlug={slug} />;
}

function SlugAsk() {
  const { slug = '' } = useParams();
  return <AskScreen shopSlug={slug} />;
}

function SlugOption() {
  const { slug = '' } = useParams();
  return <OptionScreen shopSlug={slug} />;
}

function SlugDish() {
  const { slug = '' } = useParams();
  return <DishScreen shopSlug={slug} />;
}

function SlugOwnerHome() {
  const { slug = '' } = useParams();
  return <Navigate to={`/shop/${slug}/manage/menu`} replace />;
}

function SlugOwnerEntry() {
  const { slug = '' } = useParams();
  return <EntrySettingsScreen shopSlug={slug} />;
}

function SlugOwnerPhoto() {
  const { slug = '' } = useParams();
  return <PhotoScreen shopSlug={slug} />;
}

function SlugOwnerMenu() {
  const { slug = '' } = useParams();
  return <MenuSettingsScreen shopSlug={slug} />;
}

function SlugOwnerText() {
  const { slug = '', dishId } = useParams();
  const navigate = useNavigate();

  const [resolveFailed, setResolveFailed] = useState(false);

  useEffect(() => {
    if (dishId) return;
    let cancelled = false;
    void fetchDishes(slug)
      .then((dishes) => {
        if (cancelled) return;
        const first = dishes[0];
        if (first) {
          void navigate(`/shop/${slug}/manage/dish/${first.id}`, { replace: true });
        } else {
          setResolveFailed(true);
        }
      })
      .catch(() => {
        if (!cancelled) setResolveFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [dishId, navigate, slug]);

  if (!dishId && !resolveFailed) {
    return (
      <main className="mx-auto max-w-[460px] px-4 py-10">
        <p className="text-body-s text-ink-500" aria-live="polite">
          Opening a dish…
        </p>
      </main>
    );
  }
  return <TextEntryScreen shopSlug={slug} />;
}

function RootByHost() {
  const portal = portalForHost();

  if (portal === 'owner') return <Navigate to="/shop/shop_A/manage" replace />;
  if (portal === 'admin') return <Navigate to="/admin" replace />;

  // No chooser page: every unrecognised host opens the customer portal.
  return <Navigate to="/shop/shop_A/menu" replace />;
}

function RequireOwnerRoute({ children }: { children: React.ReactNode }) {
  const { slug = '' } = useParams();

  return (
    <HostPortal requires="owner">
      <RequireOwner shopSlug={slug}>{children}</RequireOwner>
    </HostPortal>
  );
}

function SlugOwnerPassword() {
  const { slug = '' } = useParams();
  return <PasswordScreen shopSlug={slug} />;
}

function SlugOwnerEdit() {
  const { slug = '' } = useParams();
  return <ItemEditingScreen shopSlug={slug} />;
}

function SlugOwnerSignage() {
  const { slug = '' } = useParams();
  return <SignageScreen shopSlug={slug} />;
}

function NotFound() {
  return (
    <main className="mx-auto max-w-[640px] px-5 py-16 text-center">
      <h1 className="text-h2 text-plum-900">Page not found</h1>
      <p className="text-body mt-2 text-ink-600">
        Check the QR code or ask the shop for help.
      </p>
      <NavLink to="/" className="text-body-s mt-6 inline-block font-semibold text-plum-700">
        Go back
      </NavLink>
    </main>
  );
}
